import Tabs, {
  Pane,
  serializerTabsContents,
} from '@/components/ui/custom/dataDisplay/Tabs/Tabs';
import useCourseEditing from '@/data/hook/useCourseEditing';
import CoursePath from '@/models/CoursePath';
import Lesson, { LessonTab } from '@/models/Lesson';
import {
  BookOpenIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  InformationCircleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { ChallengeEditing } from './ChallengeEditing';
import CollapsibleLesson from './CollapsibleLesson';
import LessonDetails from './LessonDetails';
import LessonReplacerItem from './LessonReplacerItem';
import ListActivities from './ListActivities';
import MaterialContainer from './material/MaterialContainer';
import ResourceContainer from '@/components/_deprecated/resource/lesson/LessonResourceContainer';
import LessonBlock from '@/models/_deprecated/LessonBlock';

interface DraggableLessonProps {
  lesson: Lesson;
  index: number;
  block?: LessonBlock;
  course: CoursePath;
  deleteLesson: (lesson: Lesson) => void;
  updateLessons: () => Promise<void>;
  isDropDisabled?: boolean;
}

function DraggableLesson({
  lesson,
  index,
  block,
  course,
  isDropDisabled,
  ...props
}: DraggableLessonProps) {
  const { deleteLesson, updateLessons } = props;
  const { allowBigChanges, activeLessonTab, handleOpenTab, handleLessonTab } =
    useCourseEditing();
  const { activeLessonOrder, activeTab } = activeLessonTab || {
    activeLessonOrder: null,
    activeTab: 'details',
  };
  const { t: editRulesT } = useTranslation('translation', {
    keyPrefix: 'adminPage.editCourseRules',
  });

  const isActive = activeLessonOrder === lesson.order;

  const { t } = useTranslation('translation', {
    keyPrefix: 'common.lessonTabs',
  });

  const onChangeTab = (tab: string) =>
    handleLessonTab({
      activeTab: tab as LessonTab,
    });

  const panes: Pane[] = [
    {
      key: 'details',
      text: t('details'),
      icon: InformationCircleIcon,
      content: (
        <LessonDetails
          {...{
            lesson,
            course,
            blockId: block?.id,
            deleteLesson,
            updateLessons,
          }}
        />
      ),
    },
    {
      key: 's-material',
      text: t('material'),
      icon: BookOpenIcon,
      content: (
        <MaterialContainer
          key={lesson.book?.id}
          updateLessons={updateLessons}
          bookType="STUDENT"
          blockId={block?.id}
          {...{ course, lesson }}
        />
      ),
    },
    {
      key: 't-material',
      text: t('teacherMaterial'),
      icon: BookOpenIcon,
      content: (
        <MaterialContainer
          key={lesson.teacherBook}
          updateLessons={updateLessons}
          bookType="TEACHER"
          blockId={block?.id}
          {...{ course, lesson }}
        />
      ),
    },
    {
      key: 'resource',
      text: t('resource'),
      icon: FolderIcon,
      content: (
        <ResourceContainer
          updateLessons={updateLessons}
          coursePath={course}
          lesson={lesson}
          blockId={block?.id}
        />
      ),
    },
    {
      key: 'activities',
      text: t('activity'),
      icon: ClipboardDocumentListIcon,
      content: (
        <ListActivities
          updateLessons={updateLessons}
          lesson={lesson}
          coursePath={course}
          blockId={block?.id}
        />
      ),
    },
    {
      key: 'challenge',
      text: t('challenge'),
      icon: BoltIcon,
      content: (
        <ChallengeEditing
          onUpdate={updateLessons}
          lesson={lesson}
          blockId={block?.id}
        />
      ),
    },
  ];

  return (
    <LessonReplacerItem
      key={lesson.id}
      dragableHandler={{
        disabled: !allowBigChanges || !!isDropDisabled,
        message: editRulesT('cantModify'),
      }}
      draggableId={lesson.id.toString()}
      index={index}
    >
      <div
        className="w-full"
        onClick={() => handleOpenTab(isActive, lesson.order)}
      >
        <CollapsibleLesson
          testId="lessonBar"
          id={`lessonBar${index}`}
          open={isActive}
          lesson={lesson}
        >
          <Tabs
            active={activeTab}
            contents={serializerTabsContents(panes)}
            onChange={onChangeTab}
          >
            <Tabs.Container>
              <Tabs.List>
                {panes.map(({ key, text, icon: Icon }) => (
                  <Tabs.Trigger key={key} id={key}>
                    <Icon className="w-6" />
                    {text}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
              <Tabs.ShowContent />
            </Tabs.Container>
          </Tabs>
        </CollapsibleLesson>
      </div>
    </LessonReplacerItem>
  );
}

export default DraggableLesson;
